import { Tooltip } from '@pique/component';
import { useActiveBlocksuiteEditor } from '@pique/core/hooks/use-block-suite-editor';
import { MoreVertical, StickyNote } from 'lucide-react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './edgeless-tools-menu.css';

interface ToolAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
}

export const EdgelessToolsMenu = memo(function EdgelessToolsMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [editorContainer] = useActiveBlocksuiteEditor();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const triggerShapeTool = useCallback(() => {
    if (!editorContainer?.host) return;

    const edgelessRoot = editorContainer.host.querySelector(
      'affine-edgeless-root'
    ) as any;

    if (!edgelessRoot?.tools) return;

    edgelessRoot.tools.setEdgelessTool({
      type: 'shape',
      shapeType: 'rect',
    });

    setIsOpen(false);
  }, [editorContainer]);

  const toolActions: ToolAction[] = [
    {
      id: 'sticky-note',
      label: 'Add Note',
      icon: <StickyNote size={16} strokeWidth={2} />,
      action: triggerShapeTool,
    },
  ];

  if (!editorContainer?.host) return null;

  return (
    <div className={styles.container} ref={menuRef}>
      <Tooltip content="Tools" side="left">
        <button
          className={styles.menuButton}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-open={isOpen}
          aria-label="Tools"
        >
          <MoreVertical size={20} strokeWidth={2} />
        </button>
      </Tooltip>

      {isOpen && (
        <div className={styles.dropdown}>
          {toolActions.map(tool => (
            <button
              key={tool.id}
              className={styles.menuItem}
              onClick={e => {
                e.stopPropagation();
                tool.action();
              }}
            >
              <span className={styles.menuIcon}>{tool.icon}</span>
              <span className={styles.menuLabel}>{tool.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
